import { ReportProblemButton } from './ReportProblem';
import { lastErrorRef } from '../lib/issue-reporter';

/**
 * Humane error block with a Retry CTA and a "Contact support" link.
 *
 * Replaces raw `API error 500: …` text across the workbench (Phase 4).
 */

interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
  /** Override the default support email link if needed. */
  supportEmail?: string;
  /** Extra technical detail surfaced inside a <details> for debugging. */
  detail?: string;
  /** Optional test id so tests can scope to a particular error. */
  testId?: string;
}

/** F-215: pull the support email from a Vite env var with a sensible
 *  default. Set `VITE_SUPPORT_EMAIL=...` in .env.production to swap. */
const DEFAULT_SUPPORT_EMAIL =
  (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) ||
  'archive@brisbanepride.org.au';

export default function ErrorState({
  message,
  onRetry,
  supportEmail = DEFAULT_SUPPORT_EMAIL,
  detail,
  testId = 'error-state',
}: ErrorStateProps) {
  const display = message || 'Something went wrong loading this view.';
  return (
    <div
      className="error-block"
      role="alert"
      data-testid={testId}
    >
      <p className="error-block__message">{display}</p>
      <div className="error-block__actions">
        {onRetry && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onRetry}
            data-testid={`${testId}-retry`}
          >
            Retry
          </button>
        )}
        <a
          className="error-block__support"
          href={`mailto:${supportEmail}`}
          data-testid={`${testId}-support`}
        >
          Contact support
        </a>
        <ReportProblemButton
          className="error-block__support report-problem-link"
          label="Report this problem"
          relatedRef={lastErrorRef()}
        />
      </div>
      {detail && (
        <details className="error-block__detail">
          <summary>Technical details</summary>
          <pre>{detail}</pre>
        </details>
      )}
    </div>
  );
}
